import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useModal } from "../../context/ModalContext";
import PrintQuoteFrame from "../../components/PrintQuoteFrame";
import "./OrderPage.css"; // 기본 iframe/견적 스타일 재사용
import "./EmbedOrderPage.css";
import RegionOnly from "../../components/RegionOnly";
import { trackEvent, trackAdsConversion, ADS_CONV } from "../../utils/gtag";

const NAVER_RETURN_TTL_MS = 30 * 60 * 1000; // 30분

function EmbedOrderPageBody() {
  const { t } = useTranslation();
  const { showAlert } = useModal();
  const history = useHistory();
  const location = useLocation();
  // 견적 = print 접수 화면(PrintQuoteFrame). source=cookistory-naver 로 네이버 임베드 유입임을 표시한다.
  const [makerzQuote, setMakerzQuote] = useState(null);

  // 복사 피드백 (번호 / 금액 / 전체 각각)
  const [copiedField, setCopiedField] = useState(null);

  // ?return= 으로 전달된 네이버 스토어 복귀 URL
  const returnUrl = new URLSearchParams(location.search).get("return") || "";

  // 네이버 스토어 유입자 표식을 localStorage에 저장 — /order 에서 조회
  useEffect(() => {
    if (returnUrl) {
      try {
        localStorage.setItem(
          "naverReturn",
          JSON.stringify({
            url: returnUrl,
            expiresAt: Date.now() + NAVER_RETURN_TTL_MS,
          })
        );
      } catch (e) {
        // localStorage 사용 불가 환경(시크릿 모드 등) — 무시
      }
    }
  }, [returnUrl]);

  async function copyToClipboard(text, field) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1800);
    } catch (e) {
      showAlert(t("embedOrder.copyFailed", "복사에 실패했습니다."));
    }
  }

  /* 결제 페이지는 닫혀 있다 (2026-09-08) — 결제·주문은 네이버 스마트스토어에서.
     코드는 남겨 두고 버튼만 감춘다. 다시 열 때는 App.js 의 PAYMENT_OPEN 과 여기 둘 다 되돌린다. */
  const PAYMENT_OPEN = false;

  function goCookistoryPayment() {
    if (!makerzQuote || !makerzQuote.orderNum) {
      showAlert(t("order.alerts.noQuote", "견적을 먼저 완료해주세요."));
      return;
    }
    history.push({
      pathname: "/order/payment",
      state: { quote: makerzQuote },
    });
  }

  function goNaverStore() {
    // 구글 전환 — 스마트스토어로 이동(= 결제 직전). 결제는 네이버에서 일어나 여기서 못 잡는다.
    trackEvent("begin_checkout", {
      value: Number(makerzQuote?.totalPrice) || 0,
      currency: "KRW",
      transaction_id: makerzQuote?.orderNum,
    });
    trackAdsConversion(ADS_CONV.store, {
      value: Number(makerzQuote?.totalPrice) || 0,
      currency: "KRW",
      transaction_id: makerzQuote?.orderNum,
    });
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      // return 파라미터가 없으면 네이버 쇼핑 홈으로
      window.location.href = "https://smartstore.naver.com/";
    }
  }

  return (
    <div className="embed-order-page">
      <div className="embed-order-inner">
        {/* 쿠키스토리 홍보 배너 */}
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="embed-promo-banner"
        >
          <span className="embed-promo-icon">🎨</span>
          <span className="embed-promo-text">
            {t(
              "embedOrder.promoText",
              "나만의 쿠키커터 디자인도 만들 수 있어요"
            )}
          </span>
          <span className="embed-promo-cta">
            {t("embedOrder.promoCta", "디자인하러 가기")} →
          </span>
        </a>

        <h1 className="embed-order-title">{t("order.title")}</h1>
        <p className="embed-order-sub">
          {t("embedOrder.subtitle", "STL 파일을 올리시면 견적이 자동으로 계산됩니다.")}
        </p>

        {/* === 자동 견적 (사내 엔진) === */}
        <section className="order-section order-quote-section embed-quote-section">
          <PrintQuoteFrame source="cookistory-naver" onQuote={setMakerzQuote} />
        </section>

        {/* === 견적 복사 영역 === */}
        {makerzQuote && (
          <div className="embed-copy-box">
            <div className="embed-copy-title">
              {t("embedOrder.copyBoxTitle", "네이버 스토어 결제 시 아래 정보를 복사해 사용하세요")}
            </div>
            <div className="embed-copy-list">
              <div className="embed-copy-row">
                <span className="embed-copy-label">{t("order.orderNumber", "주문번호")}</span>
                <span className="embed-copy-value">{makerzQuote.orderNum}</span>
                <button
                  type="button"
                  className="embed-copy-btn"
                  onClick={() => copyToClipboard(makerzQuote.orderNum, "orderNum")}
                >
                  {copiedField === "orderNum"
                    ? t("embedOrder.copied", "복사됨 ✓")
                    : t("embedOrder.copy", "복사")}
                </button>
              </div>
              <div className="embed-copy-row">
                <span className="embed-copy-label">{t("orderPayment.total", "결제 금액")}</span>
                <span className="embed-copy-value embed-copy-value-price">
                  {makerzQuote.totalPrice?.toLocaleString()}{t("order.priceUnit")}
                </span>
                <button
                  type="button"
                  className="embed-copy-btn"
                  onClick={() => copyToClipboard(String(makerzQuote.totalPrice || 0), "price")}
                >
                  {copiedField === "price"
                    ? t("embedOrder.copied", "복사됨 ✓")
                    : t("embedOrder.copy", "복사")}
                </button>
              </div>
            </div>
            <button
              type="button"
              className="embed-copy-all-btn"
              onClick={() =>
                copyToClipboard(
                  `${t("order.orderNumber", "주문번호")}: ${makerzQuote.orderNum}\n${t("orderPayment.total", "결제 금액")}: ${makerzQuote.totalPrice?.toLocaleString()}${t("order.priceUnit")}`,
                  "all"
                )
              }
            >
              {copiedField === "all"
                ? t("embedOrder.copiedAll", "전체 정보 복사 완료 ✓")
                : t("embedOrder.copyAll", "전체 정보 한 번에 복사")}
            </button>
          </div>
        )}

        {/* === 견적 안내 & CTA === */}
        {makerzQuote && (
          <div className="embed-order-notice">
            {t(
              "embedOrder.quoteNotice",
              "위 견적 금액으로 결제 방식을 선택해주세요."
            )}
          </div>
        )}

        <div className="embed-order-cta-row">
          {PAYMENT_OPEN && (
            <button
              type="button"
              className="embed-cta embed-cta-cookistory"
              disabled={!makerzQuote}
              onClick={goCookistoryPayment}
            >
              {t("embedOrder.payCookistory", "쿠키스토리에서 결제")}
            </button>
          )}
          <button
            type="button"
            className="embed-cta embed-cta-naver"
            disabled={!makerzQuote}
            onClick={goNaverStore}
          >
            {t("embedOrder.payNaver", "네이버 스토어에서 결제")}
          </button>
        </div>

        <p className="embed-order-hint">
          {t(
            "embedOrder.hint",
            "네이버 스토어에서 결제하실 경우, 위 견적 금액을 확인 후 스토어로 돌아가 주문해주세요."
          )}
        </p>
      </div>
    </div>
  );
}


// 해외배송을 하지 않으므로 국내 접속에만 보인다 (설계 4).
// 메뉴에서는 이미 빠져 있고, 여기는 주소로 직접 들어온 경우를 위한 것이다.
export default function EmbedOrderPage(props) {
  return (
    <RegionOnly>
      <EmbedOrderPageBody {...props} />
    </RegionOnly>
  );
}
